import { Button, Input } from "@pushpress/pushpress-ui";
import { Icons } from "@pushpress/shared-components";

import React, { useCallback, useEffect, useState } from "react";
import { cn } from "../../../utils/css";
import { Intent } from "../types";
import { INTENTS } from "../constants";

// Optional additional styles for the component
interface ChatPromptInputProps {
  className?: string;
  inputClassName?: string;
  onSend?: (
    message: string,
    interruptionStatus?: "approved" | "rejected",
  ) => void; // Callback for sending messages
  hideQuickActions?: boolean;
}

/**
 *
 * @param className - Optional className for additional styles
 * @param onSend - Optional callback function to handle sending messages. The default
 * behavior is to open a chat modal with the input text.
 * @returns
 */
const ChatPromptInput: React.FC<ChatPromptInputProps> = ({
  className,
  inputClassName,
  hideQuickActions,
  onSend,
}) => {
  const [chatInput, setChatInput] = useState("");
  const [startText, setStartText] = useState("");
  const [quickActionIntent, setQuickActionIntent] = useState<Intent>();
  const [showQuickActions] = useState(!hideQuickActions);

  const handleSend = useCallback(() => {
    if (startText === "" && chatInput.trim() === "") {
      return;
    } // Prevent sending empty messages
    if (onSend) {
      onSend(chatInput);
    }
    setChatInput(""); // Clear chat input after sending
  }, [startText, chatInput, onSend]);

  // Toggle between intents
  const handleQuickActionClick = (intent: Intent) => {
    setQuickActionIntent((prev) => (prev === intent ? undefined : intent));
  };

  useEffect(() => {
    // Initialize startText based on intentType
    if (quickActionIntent) {
      switch (quickActionIntent) {
        case INTENTS.NEW_SUBSCRIPTION:
          setStartText("Add a plan to ");
          break;
        case INTENTS.PAUSE_SUBSCRIPTION:
          setStartText("Pause plan for ");
          break;
        case INTENTS.UPDATE_SUBSCRIPTION:
          setStartText("Switch plan for ");
          break;
        case INTENTS.RESUME_SUBSCRIPTION:
          setStartText("Resume plan for ");
          break;
        case INTENTS.CANCEL_SUBSCRIPTION:
          setStartText("Cancel plan for ");
          break;
        default:
          setStartText("");
      }
      setChatInput(startText);
    } else {
      setStartText("");
      setChatInput(""); // Clear chat input if no intent is selected
    }
  }, [quickActionIntent, startText]);

  return (
    <>
      <div className={cn("tw-w-full tw-min-w-0", className)}>
        <div className="tw-flex tw-mb-4 tw-flex-row tw-gap-4 tw-min-w-0">
          <div className="tw-relative tw-flex-1 tw-min-w-0 tw-flex tw-flex-col">
            <div className="tw-bg-white tw-flex tw-flex-col tw-gap-0 tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-200 tw-transition-shadow">
              <div className="tw-relative tw-border-none tw-border-0">
                <Input
                  type="text"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault(); // Prevent form submission
                      handleSend();
                    }
                  }}
                  className={cn(
                    "tw-w-full tw-bg-white tw-h-[48px] tw-pt-2 tw-pr-4 tw-pl-4 tw-text-base !tw-border-none !tw-outline-none !tw-shadow-none focus:!tw-border-none focus:!tw-outline-none focus:!tw-shadow-none focus:!tw-ring-0 focus-visible:!tw-ring-0 focus-visible:!tw-outline-none",
                    inputClassName,
                  )}
                  value={chatInput}
                  onChange={(e) => {
                    setChatInput(e.target.value);
                    if (e.target.value === "" && quickActionIntent) {
                      setQuickActionIntent(undefined);
                    }
                  }}
                  placeholder={
                    startText ||
                    "What membership action would you like to take?"
                  }
                />
              </div>
              {showQuickActions && (
                <div className="tw-flex tw-flex-row tw-gap-2 tw-items-start tw-px-4 tw-pb-3 tw-border-none tw-border-0">
                  <Button
                    className="!tw-bg-neutral-100 !tw-text-neutral-800 tw-border-none !tw-h-7"
                    onClick={() => {
                      handleQuickActionClick(INTENTS.PAUSE_SUBSCRIPTION);
                    }}
                  >
                    Pause plan
                  </Button>
                  <Button
                    className="tw-ml-auto tw-bg-primary tw-hover:tw-bg-primary !tw-w-7 !tw-h-7 !tw-p-1 tw-flex tw-items-center tw-justify-center"
                    onClick={handleSend}
                    disabled={!chatInput && !startText}
                  >
                    <Icons.ArrowRight
                      className="tw-text-white"
                      style={{ transform: "rotate(-90deg)" }}
                    />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatPromptInput;
